import { gql } from '@apollo/client';
import each from 'lodash/each';
import BaseService from './baseService';

class DriverService extends BaseService {
  constructor(uri) {
    super(uri);
    this.driverDataMappings = {
      driverId: {
        name: '$driverId',
        type: 'Int',
        typeRequire: 'Int!',
        input: 'driverId'
      },
      firstName: {
        name: '$firstName',
        type: 'String',
        typeRequire: 'String!',
        input: 'firstName'
      },
      lastName: {
        name: '$lastName',
        type: 'String',
        typeRequire: 'String!',
        input: 'lastName'
      },
      password: {
        name: '$password',
        type: 'String',
        input: 'password'
      },
      driversLicenseExpiration: {
        name: '$driversLicenseExpiration',
        type: 'DateTime',
        typeRequire: 'DateTime!',
        input: 'driversLicenseExpiration'
      },
      driversLicenseIssueDate: {
        name: '$driversLicenseIssueDate',
        type: 'DateTime',
        typeRequire: 'DateTime!',
        input: 'driversLicenseIssueDate'
      },
      driversLicenseStateProvince: {
        name: '$driversLicenseStateProvince',
        type: 'String',
        typeRequire: 'String!',
        input: 'driversLicenseStateProvince'
      },
      email: {
        name: '$email',
        type: 'String',
        typeRequire: 'String!',
        input: 'email'
      },
      providerId: {
        name: '$providerId',
        type: 'Int',
        typeRequire: 'Int!',
        input: 'providerId'
      },
      providerLocationId: {
        name: '$providerLocationId',
        type: 'Int',
        input: 'providerLocationId'
      },
      avatar: {
        name: '$avatar',
        type: 'String',
        input: 'avatar'
      },
      addresses: {
        name: '$addresses',
        type: '[AddressIn]',
        input: 'addresses'
      },
      phones: {
        name: '$phones',
        type: '[PhoneIn]',
        input: 'phones'
      }
    };
    this.driverGql = `
      driverId
      firstName
      lastName
      providerLocationId
      driversLicenseExpiration
      driversLicenseIssueDate
      driversLicenseStateProvince
      email
      clockedIn
      avatar
      providerId
      provider
      providerAvatar
      dateModified
      dateCreated
      phones{
        telephoneNumberId
        telephoneNumber
        telephoneName
        phoneType
        phoneTypeId
      }`;
    this.driverActionGql = `
      success
      token`;
  }

  buildDriverGQL = (data, mutation, required, actionReturned) => {
    const keys = Object.keys(data);
    const driver = [];
    const input = [];
    each(keys, (key) => {
      const map = this.driverDataMappings[key];
      if (map && data[key]) {
        driver.push(`${map.name}: ${required && map.typeRequire ? map.typeRequire : map.type}`);
        input.push(`${map.input}: ${map.name}`);
      }
    });
    return `
      mutation Driver(${driver.join(', ')}){
        ${mutation || 'updateDriver'}(
            ${input.join('\n')}
          ) {
            ${actionReturned ? this.driverActionGql : this.driverGql}
        }
    }`;
  };

  deleteDriver = (ids) => new Promise((resolve, reject) => {
    const DELETE_SERVICE = gql`
      mutation Driver($ids: [Int]!){
        removeDrivers(
          Ids: $ids
        ){
          success
          token
        }
      }
    `;

    this.client
      .mutate({
        mutation: DELETE_SERVICE,
        variables: { ids }
      })
      .then((result) => {
        if (result.data.removeDrivers) {
          resolve(result.data.removeDrivers);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  addDriver = (data) => new Promise((resolve, reject) => {
    const NEW_SERVICE = gql`
      ${this.buildDriverGQL(data, 'addDriver', true)}
    `;

    this.client
      .mutate({
        mutation: NEW_SERVICE,
        variables: data
      })
      .then((result) => {
        if (result.data.addDriver) {
          resolve(result.data.addDriver);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  saveDriver = (data) => new Promise((resolve, reject) => {
    const SERVICE = gql`${this.buildDriverGQL(data, 'updateDriver', true, true)}`;

    this.client
      .mutate({
        mutation: SERVICE,
        variables: data
      })
      .then((result) => {
        if (result.data.updateDriver) {
          resolve(result.data.updateDriver);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  getDrivers = (clockedIn) => new Promise((resolve, reject) => {
    let DRIVERS = null;
    const options = {};
    if (clockedIn === true || clockedIn === false) {
      DRIVERS = gql`
        query Driver($providerId: Int, $clockedIn: Int){
          drivers(
            providerId: $providerId
            clockedIn: $clockedIn
          ){
              ${this.driverGql}
          }
      }`;
      options.variables = {
        clockedIn: clockedIn === true ? 1 : 0
      };
    } else {
      DRIVERS = gql`
        query Driver($providerId: Int){
          drivers(
              providerId: $providerId
            ){
              ${this.driverGql}
          }
      }`;
    }
    options.query = DRIVERS;

    this.client
      .query(options)
      .then((result) => {
        if (result.data.drivers) {
          resolve(result.data.drivers);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  searchDrivers = (value) => new Promise((resolve, reject) => {
    const CUSTOMERS = gql`
      query Driver($value: String!){
        searchDrivers(
            Value: $value
          ){
            ${this.driverGql}
        }
    }`;

    this.client
      .query({
        fetchPolicy: 'no-cache',
        query: CUSTOMERS,
        variables: { value }
      })
      .then((result) => {
        if (result.data.searchDrivers) {
          resolve(result.data.searchDrivers);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });

  getDriver = (driverId) => new Promise((resolve, reject) => {
    const SERVICE = gql`
      query Driver($driverId: Int!){
        driver(driverId: $driverId){
            ${this.driverGql}
        }
    }`;

    this.client
      .query({
        fetchPolicy: 'no-cache',
        query: SERVICE,
        variables: { driverId }
      })
      .then((result) => {
        if (result.data.driver) {
          resolve(result.data.driver);
        } else {
          reject(result.data.error);
        }
      })
      .catch((error) => {
        console.log('error', error);
        reject(error);
      });
  });
}

const driverService = new DriverService(`${process.env.REACT_APP_PROVIDER_API}/graphql`);

export default driverService;
